import React, { useState } from 'react';
import { ShowController, Link } from 'react-admin';
// react-admin's <Link> is a plain function component, so it cannot receive the
// ref that MUI's Button passes to its `component`. react-router's Link is
// wrapped in forwardRef (v5.1+), so it is the right one for the nav buttons.
import { Link as RouterLink } from 'react-router-dom';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import TuneIcon from '@material-ui/icons/Tune';
import PanToolIcon from '@material-ui/icons/PanTool';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import HistoryIcon from '@material-ui/icons/History';
import ViewListIcon from '@material-ui/icons/ViewList';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import moment from 'moment';

import useSupplyStyles from '../controls/SupplyStyles';
import InventoryTable from './InventoryTable';
import SublotPanel from './SublotPanel';
import AdjustmentDialog from './AdjustmentDialog';
import ErrorBoundary from '../ErrorBoundary';

const fmt = value => (value ? moment(value).format('MM/DD/YYYY') : '-');

/** Left card: lot identity plus the action links. */
const ReceiptAside = ({ record, onAdjust, onHold, onReleaseHold }) => {
    const classes = useSupplyStyles();

    return (
        <Card className={classes.asideCard}>
            <CardContent>
                <Typography className={classes.asideTitle}>Supply Receipt</Typography>

                <Typography className={classes.asideLabel}>Supply Type:</Typography>
                <Typography className={classes.asideValue}>{record.supply_type}</Typography>

                <Typography className={classes.asideLabel}>Lot #:</Typography>
                <Typography className={classes.asideValue}>{record.lot_no}</Typography>

                <Typography className={classes.asideLabel}>Receipt Date:</Typography>
                <Typography className={classes.asideValue}>{fmt(record.receipt_date)}</Typography>

                <Typography className={classes.asideTitle}>Actions</Typography>

                {/* Things that change stock: solid buttons, primary first. */}
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    startIcon={<TuneIcon />}
                    className={classes.actionButton}
                    onClick={onAdjust}
                >
                    Adjustment
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    fullWidth
                    startIcon={<PanToolIcon />}
                    className={classes.actionButton}
                    onClick={onHold}
                >
                    Apply Hold
                </Button>
                {record.qty_hold > 0 && (
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        fullWidth
                        startIcon={<LockOpenIcon />}
                        className={classes.actionButton}
                        onClick={onReleaseHold}
                    >
                        Release Hold
                    </Button>
                )}

                <Divider className={classes.actionDivider} />

                {/* Navigation away from this lot: same weight, clearly clickable,
                    with a chevron so they read as "go somewhere" not "do something". */}
                <Button
                    component={RouterLink}
                    to={{
                        pathname: '/supplyadjustments',
                        search: `filter=${JSON.stringify({ receipt_id: record.id })}`,
                    }}
                    size="small"
                    fullWidth
                    startIcon={<HistoryIcon />}
                    endIcon={<ChevronRightIcon />}
                    className={classes.navButton}
                >
                    Adjustment History
                </Button>
                <Button
                    component={RouterLink}
                    to={{
                        pathname: '/supplylots',
                        search: `filter=${JSON.stringify({ supply_type_id: record.supply_type_id })}`,
                    }}
                    size="small"
                    fullWidth
                    startIcon={<ViewListIcon />}
                    endIcon={<ChevronRightIcon />}
                    className={classes.navButton}
                >
                    All Lots
                </Button>
            </CardContent>
        </Card>
    );
};

/** Right pane: SUMMARY block, then the inventory grid, then sublots. */
const ReceiptSummary = ({ record }) => {
    const classes = useSupplyStyles();

    return (
        <React.Fragment>
            <Typography className={classes.sectionTitle}>Summary</Typography>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                        <b>Supply Type:</b> {record.supply_type}
                    </Typography>
                    <Typography variant="body2">
                        <b>Lot #:</b> {record.lot_no}
                    </Typography>
                    <Typography variant="body2">
                        <b>Receipt Date:</b> {fmt(record.receipt_date)}
                    </Typography>
                    <Typography variant="body2">
                        <b>Manifest #:</b>{' '}
                        <Link to={`/supplyshipments/${record.shipment_id}/show`}>
                            {record.manifest_no}
                        </Link>
                    </Typography>
                    <Typography variant="body2">
                        <b>Received Qty:</b> {record.received_qty}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                        <b>Vendor:</b> {record.vendor || record.origin_site || '-'}
                    </Typography>
                    <Typography variant="body2">
                        <b>Expiry Date:</b> {fmt(record.expiry_date)}
                    </Typography>
                    <Typography variant="body2">
                        <b>Category:</b> {record.category}
                    </Typography>
                    {record.cal_low !== null && record.cal_low !== undefined && (
                        <Typography variant="body2">
                            <b>Calibration Low Value:</b> {record.cal_low}
                        </Typography>
                    )}
                    {record.cal_high !== null && record.cal_high !== undefined && (
                        <Typography variant="body2">
                            <b>Calibration High Value:</b> {record.cal_high}
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

const SupplyReceiptShow = props => {
    const classes = useSupplyStyles();
    const [dialog, setDialog] = useState(null);

    return (
        <ErrorBoundary>
            <ShowController {...props} title=" ">
                {({ record }) =>
                    record && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <ReceiptAside
                                    record={record}
                                    onAdjust={() => setDialog('adjust')}
                                    onHold={() => setDialog('hold')}
                                    onReleaseHold={() => setDialog('releasehold')}
                                />
                            </Grid>
                            <Grid item xs={12} md={9}>
                                <Card className={classes.asideCardWide}>
                                    <CardContent>
                                        <ReceiptSummary record={record} />
                                        <InventoryTable record={record} />
                                        <SublotPanel record={record} />
                                    </CardContent>
                                </Card>
                            </Grid>

                            <AdjustmentDialog
                                open={dialog !== null}
                                mode={dialog}
                                record={record}
                                onClose={() => setDialog(null)}
                            />
                        </Grid>
                    )
                }
            </ShowController>
        </ErrorBoundary>
    );
};

export default SupplyReceiptShow;
